import { EJobAttemptType, EJobRecordStatus, type IJobRecord } from '@quesmed/types/models';
import type { ReactNode } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { EDataView, EFormMode } from '@/config/globals';
import type { OmitFunctions } from '@/types';
import { qsStorage } from '@/utils/index';
import { useAsset, useQuestion } from '.';
import { createSelectors } from './selector';

interface RecordSlice {
  view: EDataView;
  hideRemarks: boolean;
  recordModal: boolean;
  bulkUploadModal: boolean;
  columns: string[];
  selectedRecords: string[];
  moveWarning: {
    isOpen: boolean;
    direction: 'next' | 'previous';
  };
  attemptType: EJobAttemptType;
  availableRecords: number;
  availableSets: number;
  status: EJobRecordStatus;
  search: string;
  preview: boolean;
  defaultExpandedKeys: string[];
  recordId: string;
  confirmModal: {
    isOpen: boolean;
    title: string;
    message: ReactNode;
    key: 'duplicate' | 'delete' | 'ready' | 'publish' | 'approve' | 'reject' | 'unlock' | 'testing' | null;
    recordId: string | null;
  };
  formMode: EFormMode;
  currentRecord: IJobRecord | null;
  records: IJobRecord[];
  recordStatus: Partial<Record<EJobRecordStatus, number>>;
  stemMap: Record<string, number>;
  choicesGridCols: string;
  questionLayout: 'horizontal' | 'vertical';
  // deletedAssets: IJobAsset[];

  reset: () => void;
  setView: (view: EDataView) => void;
  setRecordModal: (isOpen: boolean, formMode: EFormMode) => void;
  setSelectedRecords: (selectedRecords: string[]) => void;
  addSelectedRecord: (recordId: string) => void;
  removeSelectedRecord: (recordId: string) => void;
  setStatus: (status: EJobRecordStatus) => void;
  setAttemptType: (attemptType: EJobAttemptType) => void;
  setSearch: (search: string) => void;
  setPreview: (preview: boolean) => void;
  setDefaultExpandedKeys: (keys: string[]) => void;
  setRecordId: (recordId: string) => void;
  setBulkUploadModal: (isOpen: boolean) => void;
  setConfirmModal: (
    isOpen: boolean,
    title: string,
    message: ReactNode,
    key: RecordSlice['confirmModal']['key'],
    recordId: RecordSlice['confirmModal']['recordId'],
  ) => void;
  setCurrentRecord: (record: IJobRecord | null) => void;
  setNextRecord: () => void;
  setPreviousRecord: () => void;
  setRecords: (records: IJobRecord[]) => void;
  setMoveWarning: (moveWarning: boolean, direction: 'next' | 'previous') => void;
  toggleHideRemarks: () => void;
  setChoicesGridCols: (gridCols: string) => void;
  setColumns: (columns: string[]) => void;
  setQuestionLayout: (layout: 'horizontal' | 'vertical') => void;
  setFormMode: (formMode: EFormMode) => void;
  // addDeletedAssets: (asset: IJobAsset) => void;
}

const initialState: OmitFunctions<RecordSlice> = {
  view: EDataView.GRID,
  hideRemarks: false,
  recordStatus: {},
  attemptType: EJobAttemptType.VOTE_ATTEMPT,
  recordModal: false,
  questionLayout: 'horizontal',
  bulkUploadModal: false,
  moveWarning: {
    isOpen: false,
    direction: 'next',
  },
  availableRecords: 0,
  availableSets: 0,
  columns: ['index', 'id', 'data', 'status', 'assets', 'remarks', 'actions'],
  selectedRecords: [],
  status: EJobRecordStatus.ALL,
  search: '',
  preview: false,
  defaultExpandedKeys: [],
  recordId: '',
  confirmModal: {
    isOpen: false,
    title: '',
    message: '',
    key: null,
    recordId: null,
  },
  formMode: EFormMode.CREATE,
  currentRecord: null,
  records: [],
  stemMap: {},
  choicesGridCols: '1',
  // deletedAssets: [],
};

const recordSlice = create<RecordSlice>()(
  persist(
    (set, get) => ({
      ...initialState,
      reset: () => {
        set(initialState);
      },
      setChoicesGridCols: (gridCols) => set({ choicesGridCols: gridCols }),
      setQuestionLayout: (layout) => set({ questionLayout: layout }),
      setView: (view) => set({ view }),
      setStatus: (status) => set({ status }),
      setDefaultExpandedKeys: (defaultExpandedKeys) => set({ defaultExpandedKeys }),
      setSearch: (search) => set({ search }),
      setFormMode: (formMode) => set({ formMode }),
      setPreview: (preview) => set({ preview }),
      // when modal is closed, reset the current record and asset ids, asset mapping and record id
      setRecordModal: (isOpen, formMode) => {
        set({
          recordModal: isOpen,
          formMode,
          preview: formMode === EFormMode.VIEW,
          ...(isOpen ? {} : { currentRecord: null, recordId: '', hideRemarks: false, assetMapping: {} }),
        });
      },
      toggleHideRemarks: () => set((state) => ({ hideRemarks: !state.hideRemarks })),
      setColumns: (columns) => set({ columns }),
      setSelectedRecords: (selectedRecords) => set({ selectedRecords }),
      addSelectedRecord: (recordId) => {
        set((state) => ({ selectedRecords: [...state.selectedRecords, recordId] }));
      },
      removeSelectedRecord: (recordId) => {
        set((state) => ({ selectedRecords: state.selectedRecords.filter((id) => id !== recordId) }));
      },
      setRecordId: (recordId) => {
        const record = get().records.find((r) => r.id === recordId);
        set({ recordId, ...(record ? { currentRecord: record } : {}) });
      },
      setConfirmModal: (isOpen, title, message, key, recordId) => {
        set({ confirmModal: { isOpen, title, message, key, recordId } });
      },
      setCurrentRecord: (record) => {
        if (!record) {
          set({ currentRecord: null });
          return;
        }
        // const assetMapping = getAssetMapping(record.assets);
        useAsset.getState().setAssetMapping(record.assets);
        useQuestion.getState().setQuestion(record.question);
        set({ currentRecord: record });
      },
      setNextRecord: () => {
        set((state) => {
          const curIndex = state.currentRecord?.index || 0;
          const nextIndex = curIndex ? curIndex + 1 : 0;
          let nextRecord = state.records.find((record) => record.index === nextIndex);
          if (!nextRecord) {
            nextRecord = state.records[curIndex];
          }
          useAsset.getState().setAssetMapping(nextRecord?.assets || []);
          useQuestion.getState().setQuestion(nextRecord?.question);
          return { currentRecord: nextRecord };
        });
      },
      setBulkUploadModal: (isOpen) => set({ bulkUploadModal: isOpen }),
      setPreviousRecord: () => {
        set((state) => {
          const curIndex = state.currentRecord?.index || 0;
          const prevIndex = curIndex ? curIndex - 1 : 0;
          let prevRecord = state.records.find((record) => record.index === prevIndex);
          if (!prevRecord) {
            prevRecord = state.records[curIndex];
          }
          useAsset.getState().setAssetMapping(prevRecord?.assets || []);
          useQuestion.getState().setQuestion(prevRecord?.question);
          return { currentRecord: prevRecord };
        });
      },
      setRecords: (records) => {
        const stemMap: Record<string, number> = {};
        let count = 1;
        const hasReferenceIds = records.some((record) => record.referenceId);

        if (hasReferenceIds) {
          const sortedRecords = [...records].sort((a, b) => {
            if (a.referenceId && !b.referenceId) return 1;
            if (!a.referenceId && b.referenceId) return -1;
            return 0;
          });

          for (const record of sortedRecords) {
            if (record.referenceId) {
              stemMap[record.id] = stemMap[record.referenceId];
            } else {
              stemMap[record.id] = count++;
            }
          }
        }

        const recordStatus = records.reduce(
          (acc, record) => {
            acc[record.status] = (acc[record.status] || 0) + 1;
            return acc;
          },
          {} as Record<EJobRecordStatus, number>,
        );

        set((state) => ({
          records: records.map((r, idx) => ({ ...r, position: idx + 1 })),
          recordStatus,
          availableRecords: records.length,
          availableSets: records.filter((r) => !r.referenceId)?.length,
          ...(state.currentRecord && {
            currentRecord: records.find((r) => r.id === state.currentRecord?.id),
          }),
          stemMap,
        }));
      },
      setMoveWarning: (isOpen, direction) => set({ moveWarning: { isOpen, direction } }),
      setAttemptType: (attemptType) => {
        const value = attemptType ? Number(attemptType) : EJobAttemptType.VOTE_ATTEMPT;
        set({ attemptType: value });
      },
    }),
    {
      name: 'record-filters',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        view: state.view,
        selectedRecords: state.selectedRecords,
        status: state.status,
        search: state.search,
        attemptType: state.attemptType,
      }),
    },
  ),
);

export const useRecord = createSelectors(recordSlice);
